import React, { useEffect, useState } from 'react';
import type { ToggleModalProps } from '../../components/modal/types';
import type { AverageReportType, GroupType } from '../../pages/types';
import { Button, ChoiceGroup } from '../../components';
import { useI18n } from '@whizz/react-i18n';
import CancelButtonModal from '../buttons/CancelButtonModal';
import ConfirmButtonModal from '../buttons/ConfirmButtonModal';
import ClearButtonModal from '../buttons/ClearButtonModal';
import { ModalFooter } from '../../components/modal/Modal';

export interface SchoolReportFilterModalProps extends Pick<ToggleModalProps, 'toggle'> {
  values: (GroupType & AverageReportType)[],
  filteredId?: number,
  resetFilter: () => void,
  show: boolean;
  onSubmit: (selectedClass: (GroupType & AverageReportType) | null, show: boolean) => void;
}

const SchoolReportFilterModal = (
  {
    values,
    filteredId,
    toggle,
    resetFilter,
    show,
    onSubmit,
  }: SchoolReportFilterModalProps,
): JSX.Element => {
  const { translate } = useI18n();
  const [ localShowOthersData, setLocalShowOthersData ] = useState(show);
  const [ selectedClass, setSelectedClass ] = useState<(GroupType & AverageReportType) | null>(null);

  useEffect(() => {
    if (null == filteredId) {
      return;
    }

    setSelectedClass(values.find((value) => filteredId === value.id) ?? null);
  }, [ filteredId ]);

  return (
    <>
      <div className={'flex container items-center justify-between pb-lg'}>
        <div className={'w-2/4'}>
          <h5 className={'font-bold'}>{translate('pages.REPORTS.FILTERS.HIGHLIGHT')}</h5>
        </div>
        <div>
          <ChoiceGroup
            id={'show_other_groups_data'}
            label={translate('pages.REPORTS.FILTERS.MODAL.show_other_class_data')}
            checked={localShowOthersData}
            onChange={(event) => setLocalShowOthersData(event.target.checked)}
          />
        </div>
      </div>
      <div className={'container flex items-center justify-between'}>
        <div className={'overflow-auto border rounded-default p-md'} style={{ maxHeight: 400 }}>
          <div className={'grid md:grid-cols-5 grid-flow-row auto-rows-max gap-2'}>
            {values.map((value) => (
              <Button
                key={`group_filter_${value.id}`}
                isOutline
                highlight={selectedClass?.id == value.id}
                onClick={() => setSelectedClass(value)}
              >
                {value.fullName}
              </Button>
            ))}
          </div>
        </div>
      </div>
      <ModalFooter>
        <div>
          <CancelButtonModal
            type={'button'}
            onClick={() => {
              onSubmit(null, true);

              toggle(false);
            }}
            data-cy="cancel-button-modal"
            className={'mr-5'}
          >
            {translate('pages.REPORTS.FILTERS.MODAL.cancel_changes')}
          </CancelButtonModal>
          <ClearButtonModal
            onClick={() => {
              resetFilter();
              toggle(false);
            }}
            disabled={filteredId === null}
          />
        </div>
        <ConfirmButtonModal
          type={'submit'}
          data-cy="confirm-button-modal"
          onClick={() => {
            onSubmit(selectedClass, localShowOthersData);

            toggle(false);
          }}
          disabled={null == selectedClass}
        >
          {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
        </ConfirmButtonModal>
      </ModalFooter>
    </>
  );
};

export default SchoolReportFilterModal;
